import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import {
  Globe, Search, Sparkles, Users, MessageSquare, Palette,
  Check, Loader2, ArrowRight, Dna,
} from 'lucide-react';
import { colors, spacing, radius, typography, gradients, transitions } from '../../../styles/theme';
import brandDnaActions from '../../../actions/brandDna';

const toErrMsg = (err) =>
  typeof err?.error === 'string' ? err.error
    : err?.error?.message || err?.message || 'An error occurred';

const PHASES = [
  { key: 'scanning', icon: Globe, label: 'Scanning website', range: [0, 15] },
  { key: 'understanding', icon: Sparkles, label: 'Understanding your brand', range: [15, 40] },
  { key: 'audience', icon: Users, label: 'Analyzing your audience', range: [40, 60] },
  { key: 'voice', icon: MessageSquare, label: 'Defining your voice', range: [60, 80] },
  { key: 'visual', icon: Palette, label: 'Capturing visual identity', range: [80, 95] },
  { key: 'done', icon: Check, label: 'Brand DNA is ready', range: [95, 100] },
];

export default function BrandDnaExtractScreen() {
  const { brandId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [url, setUrl] = useState(searchParams.get('url') || '');
  const [extractionId, setExtractionId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | extracting | completed | failed
  const [errorMsg, setErrorMsg] = useState('');
  const [currentPhase, setCurrentPhase] = useState(0);
  const pollRef = useRef(null);

  // Auto-start if URL provided via query param
  useEffect(() => {
    const urlParam = searchParams.get('url');
    if (urlParam && brandId && status === 'idle') {
      startExtraction(urlParam);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update phase based on progress
  useEffect(() => {
    for (let i = PHASES.length - 1; i >= 0; i--) {
      if (progress >= PHASES[i].range[0]) {
        setCurrentPhase(i);
        break;
      }
    }
  }, [progress]);

  const startExtraction = async (sourceUrl) => {
    const targetUrl = sourceUrl || url;
    if (!targetUrl.trim()) {
      message.warning('Enter a website URL');
      return;
    }

    try {
      setStatus('extracting');
      setProgress(0);
      setErrorMsg('');

      const res = await brandDnaActions.startExtraction(brandId, targetUrl.trim());
      if (res.success) {
        setExtractionId(res.data.extraction_id);
        startPolling(res.data.extraction_id);
      }
    } catch (err) {
      setStatus('failed');
      setErrorMsg(toErrMsg(err));
    }
  };

  const startPolling = useCallback((extId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await brandDnaActions.getExtractionStatus(brandId, extId);
        if (res.success) {
          const ext = res.data;
          setProgress(ext.progress_pct);

          if (ext.status === 'completed') {
            clearInterval(pollRef.current);
            setStatus('completed');
            setProgress(100);
          } else if (ext.status === 'failed') {
            clearInterval(pollRef.current);
            setStatus('failed');
            setErrorMsg(ext.error_message || 'Extraction failed');
          }
        }
      } catch {
        // Keep polling, might be transient
      }
    }, 2000);
  }, [brandId]);

  return (
    <div style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
    }}>
      <AnimatePresence mode="wait">
        {status === 'idle' && (
          <UrlInputPhase
            key="input"
            url={url}
            setUrl={setUrl}
            onSubmit={() => startExtraction()}
          />
        )}

        {status === 'extracting' && (
          <ExtractionPhase
            key="extracting"
            progress={progress}
            currentPhase={currentPhase}
            url={url || searchParams.get('url') || ''}
          />
        )}

        {status === 'completed' && (
          <CompletedPhase
            key="completed"
            onViewBrand={() => navigate(`/brand-dna/${brandId}`)}
          />
        )}

        {status === 'failed' && (
          <FailedPhase
            key="failed"
            errorMsg={errorMsg}
            onRetry={() => {
              setStatus('idle');
              setProgress(0);
            }}
            onViewBrand={() => navigate(`/brand-dna/${brandId}`)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── URL Input Phase ─────────────────────────────────────────

function UrlInputPhase({ url, setUrl, onSubmit }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      style={{ width: '100%', maxWidth: 560, textAlign: 'center' }}
    >
      <motion.div
        animate={{ rotate: [0, 5, -5, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: 72,
          height: 72,
          borderRadius: radius.xl,
          background: `linear-gradient(135deg, ${colors.brand.primary}20, ${colors.brand.secondary}20)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: `0 auto ${spacing.xl}px`,
        }}
      >
        <Dna size={36} color={colors.brand.primary} />
      </motion.div>

      <h1 style={{
        color: colors.text.primary,
        fontSize: typography.fontSize.xxxl,
        fontWeight: typography.fontWeight.bold,
        marginBottom: spacing.sm,
      }}>
        Analyze Your Brand
      </h1>
      <p style={{
        color: colors.text.tertiary,
        fontSize: typography.fontSize.lg,
        marginBottom: spacing.xxl,
        lineHeight: typography.lineHeight.relaxed,
      }}>
        Paste your website URL and our AI will extract your complete brand identity.
      </p>

      <div style={{
        display: 'flex',
        gap: spacing.sm,
        background: colors.background.secondary,
        border: `1px solid ${colors.border.primary}`,
        borderRadius: radius.lg,
        padding: spacing.xs,
      }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 12 }}>
          <Globe size={18} color={colors.text.tertiary} style={{ flexShrink: 0 }} />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yourwebsite.com"
            onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
            autoFocus
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: colors.text.primary,
              fontSize: 16,
              fontFamily: 'inherit',
            }}
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            padding: `${spacing.sm}px ${spacing.xl}px`,
            background: gradients.primary,
            border: 'none',
            borderRadius: radius.md,
            color: '#fff',
            fontSize: typography.fontSize.md,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Search size={18} />
          Analyze
        </motion.button>
      </div>
    </motion.div>
  );
}

// ── Extraction Phase ────────────────────────────────────────

function ExtractionPhase({ progress, currentPhase, url }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      style={{ width: '100%', maxWidth: 500, textAlign: 'center' }}
    >
      {/* URL being scanned */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: spacing.xs,
          padding: `${spacing.xxs}px ${spacing.sm}px`,
          borderRadius: radius.full,
          background: colors.surface.primary,
          border: `1px solid ${colors.border.secondary}`,
          marginBottom: spacing.xxl,
          fontSize: typography.fontSize.sm,
          color: colors.text.tertiary,
        }}
      >
        <Globe size={14} />
        {url}
      </motion.div>

      {/* Progress ring */}
      <div style={{ marginBottom: spacing.xl }}>
        <ProgressRing progress={progress} />
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, textAlign: 'left' }}>
        {PHASES.map((phase, i) => {
          const Icon = phase.icon;
          const isActive = i === currentPhase;
          const isDone = i < currentPhase;

          return (
            <motion.div
              key={phase.key}
              initial={{ opacity: 0, x: -10 }}
              animate={{
                opacity: isDone || isActive ? 1 : 0.3,
                x: 0,
              }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                padding: `${spacing.sm}px ${spacing.md}px`,
                borderRadius: radius.md,
                background: isActive ? colors.surface.primary : 'transparent',
                border: isActive ? `1px solid ${colors.border.primary}` : '1px solid transparent',
              }}
            >
              <div style={{
                width: 32,
                height: 32,
                borderRadius: radius.full,
                background: isDone
                  ? 'rgba(82, 196, 26, 0.15)'
                  : isActive
                  ? `${colors.brand.primary}20`
                  : colors.surface.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {isDone ? (
                  <Check size={16} color={colors.status.success} />
                ) : isActive ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                  >
                    <Loader2 size={16} color={colors.brand.primary} />
                  </motion.div>
                ) : (
                  <Icon size={16} color={colors.text.disabled} />
                )}
              </div>
              <span style={{
                color: isDone ? colors.status.success : isActive ? colors.text.primary : colors.text.disabled,
                fontSize: typography.fontSize.md,
                fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.normal,
              }}>
                {phase.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ── Progress Ring ────────────────────────────────────────────

function ProgressRing({ progress }) {
  const size = 120;
  const strokeWidth = 6;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={colors.surface.secondary} strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={colors.brand.primary} strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <motion.span
          key={progress}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          style={{
            fontSize: typography.fontSize.xxxl,
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary,
          }}
        >
          {progress}%
        </motion.span>
      </div>
    </div>
  );
}

// ── Completed Phase ─────────────────────────────────────────

function CompletedPhase({ onViewBrand }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{ textAlign: 'center', maxWidth: 400 }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
        style={{
          width: 80,
          height: 80,
          borderRadius: radius.full,
          background: 'rgba(82, 196, 26, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: `0 auto ${spacing.xl}px`,
        }}
      >
        <Check size={40} color={colors.status.success} />
      </motion.div>

      <h2 style={{
        color: colors.text.primary,
        fontSize: typography.fontSize.xxl,
        fontWeight: typography.fontWeight.bold,
        marginBottom: spacing.sm,
      }}>
        Brand DNA Extracted
      </h2>
      <p style={{
        color: colors.text.tertiary,
        fontSize: typography.fontSize.md,
        marginBottom: spacing.xl,
        lineHeight: typography.lineHeight.relaxed,
      }}>
        Your brand identity, audience, voice, and visual style have been analyzed and saved.
      </p>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onViewBrand}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: spacing.xs,
          padding: `${spacing.sm}px ${spacing.xxl}px`,
          background: gradients.primary,
          border: 'none',
          borderRadius: radius.md,
          color: '#fff',
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          cursor: 'pointer',
        }}
      >
        View Brand DNA
        <ArrowRight size={18} />
      </motion.button>
    </motion.div>
  );
}

// ── Failed Phase ────────────────────────────────────────────

function FailedPhase({ errorMsg, onRetry, onViewBrand }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ textAlign: 'center', maxWidth: 400 }}
    >
      <div style={{
        width: 64,
        height: 64,
        borderRadius: radius.full,
        background: 'rgba(245, 87, 108, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: `0 auto ${spacing.xl}px`,
        fontSize: 28,
      }}>
        !
      </div>

      <h2 style={{ color: colors.text.primary, fontSize: typography.fontSize.xl, marginBottom: spacing.sm }}>
        Extraction Failed
      </h2>
      <p style={{ color: colors.text.tertiary, fontSize: typography.fontSize.md, marginBottom: spacing.xl }}>
        {errorMsg || 'Something went wrong. Please try again.'}
      </p>

      <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'center' }}>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={onViewBrand}
          style={{
            padding: `${spacing.sm}px ${spacing.lg}px`,
            background: 'transparent',
            border: `1px solid ${colors.border.primary}`,
            borderRadius: radius.md,
            color: colors.text.secondary,
            fontSize: typography.fontSize.md,
            cursor: 'pointer',
          }}
        >
          Edit Manually
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          style={{
            padding: `${spacing.sm}px ${spacing.xl}px`,
            background: gradients.primary,
            border: 'none',
            borderRadius: radius.md,
            color: '#fff',
            fontSize: typography.fontSize.md,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
          }}
        >
          Try Again
        </motion.button>
      </div>
    </motion.div>
  );
}
